import { useEffect, useRef, useState } from 'react';
import { GeofenceColour } from '../api';
import type { GoogleConfig } from './GoogleMap';
import { cssVar, GEOFENCE_FILL_OPACITY, IRELAND, readFlag, SAT_KEY, saveFlag } from './mapCommon';

// Drawing a geofence on a Google map, with Google's editable shapes (not its Drawing Library,
// which Google is retiring). Circle: click for the centre, then drag the centre or the edge.
// Polygon: click for each corner, drag corners (or the midpoints for a new corner), double-click
// a corner to take it out. Used by the live map (drawing in place, from the Geofences card) and
// by the editor page below.

export interface Circle { lat: number; lon: number; radiusM: number }
export type Points = Array<[number, number]>;   // [lon, lat]

export interface Drawing {
  kind: 'circle' | 'polygon';
  circle: Circle | null;
  points: Points;
  colour: GeofenceColour;
  onCircle: (c: Circle | null) => void;
  onPoints: (p: Points) => void;
  // Zoom to the shape once it is there (an existing geofence; the editor page also for a new one).
  fitOnStart?: boolean;
}

const NEW_RADIUS_M = 200;
const round = (n: number) => Math.round(n * 1e6) / 1e6;

// Turns map `m` into a drawing surface while `d` is set, and back to a normal map after.
export function useGeofenceDrawing(m: google.maps.Map | null, d: Drawing | null) {
  const live = useRef(d);
  live.current = d;
  const circleShape = useRef<google.maps.Circle | null>(null);
  const polygon = useRef<google.maps.Polygon | null>(null);
  const fitted = useRef(false);
  const writing = useRef(false);   // true while we set the shape ourselves, so its events are ignored
  const active = d !== null;

  useEffect(() => {
    if (!m || !active) return;
    fitted.current = !live.current?.fitOnStart;
    m.setOptions({ draggableCursor: 'crosshair', disableDoubleClickZoom: true });
    const click = m.addListener('click', (e: google.maps.MapMouseEvent) => {
      const cur = live.current;
      if (!e.latLng || !cur) return;
      const at = { lat: round(e.latLng.lat()), lon: round(e.latLng.lng()) };
      if (cur.kind === 'circle') cur.onCircle({ ...at, radiusM: cur.circle?.radiusM ?? NEW_RADIUS_M });
      else cur.onPoints([...cur.points, [at.lon, at.lat]]);
    });
    const circ = new google.maps.Circle({ map: null, editable: true, draggable: true, strokeWeight: 2.5, fillOpacity: GEOFENCE_FILL_OPACITY * 2, zIndex: 5 });
    const report = () => {
      if (writing.current) return;
      const c = circ.getCenter();
      if (c) live.current?.onCircle({ lat: round(c.lat()), lon: round(c.lng()), radiusM: Math.round(circ.getRadius()) });
    };
    circ.addListener('center_changed', report);
    circ.addListener('radius_changed', report);
    const poly = new google.maps.Polygon({ map: null, editable: true, draggable: false, strokeWeight: 2.5, fillOpacity: GEOFENCE_FILL_OPACITY * 2, zIndex: 5 });
    poly.addListener('dblclick', (e: google.maps.PolyMouseEvent) => {
      if (e.vertex !== undefined && poly.getPath().getLength() > 3) poly.getPath().removeAt(e.vertex);
    });
    // Clicks on the shape itself add a corner too (Google would otherwise swallow them).
    poly.addListener('click', (e: google.maps.PolyMouseEvent) => {
      const cur = live.current;
      if (cur && e.vertex === undefined && e.edge === undefined && e.latLng) cur.onPoints([...cur.points, [round(e.latLng.lng()), round(e.latLng.lat())]]);
    });
    circleShape.current = circ;
    polygon.current = poly;
    return () => {
      click.remove();
      google.maps.event.clearInstanceListeners(circ);
      google.maps.event.clearInstanceListeners(poly);
      circ.setMap(null);
      poly.setMap(null);
      circleShape.current = null;
      polygon.current = null;
      m.setOptions({ draggableCursor: null, disableDoubleClickZoom: false });
    };
  }, [m, active]);

  // The shape being drawn follows `d`.
  useEffect(() => {
    const circ = circleShape.current;
    const poly = polygon.current;
    if (!m || !d || !circ || !poly) return;
    const { kind, circle, points, colour } = d;
    const colourHex = cssVar(`--geofence-${colour}`) || '#2a78d6';
    writing.current = true;
    circ.setOptions({ strokeColor: colourHex, fillColor: colourHex });
    poly.setOptions({ strokeColor: colourHex, fillColor: colourHex });
    if (kind === 'circle' && circle) {
      const c = circ.getCenter();
      if (!c || c.lat() !== circle.lat || c.lng() !== circle.lon) circ.setCenter({ lat: circle.lat, lng: circle.lon });
      if (Math.round(circ.getRadius()) !== circle.radiusM) circ.setRadius(circle.radiusM);
      circ.setMap(m);
    } else circ.setMap(null);
    if (kind === 'polygon' && points.length) {
      const now = poly.getPath().getArray().map((p) => [round(p.lng()), round(p.lat())]);
      if (JSON.stringify(now) !== JSON.stringify(points)) {
        const path = new google.maps.MVCArray(points.map(([lon, lat]) => new google.maps.LatLng(lat, lon)));
        poly.setPath(path);
        // Dragging a corner or a midpoint, or taking a corner out, reports the new outline.
        const read = () => { if (!writing.current) live.current?.onPoints(path.getArray().map((p) => [round(p.lng()), round(p.lat())])); };
        for (const ev of ['set_at', 'insert_at', 'remove_at']) path.addListener(ev, read);
      }
      poly.setMap(m);
    } else poly.setMap(null);
    writing.current = false;
    if (!fitted.current && ((kind === 'circle' && circle) || (kind === 'polygon' && points.length > 2))) {
      fitted.current = true;
      const b = kind === 'circle' ? circ.getBounds() : (() => {
        const lb = new google.maps.LatLngBounds();
        points.forEach(([lon, lat]) => lb.extend({ lat, lng: lon }));
        return lb;
      })();
      if (b) m.fitBounds(b, 60);
    }
  }, [m, active, d?.kind, d?.circle, d?.points, d?.colour]);
}

export interface EditorProps extends Omit<Drawing, 'fitOnStart'> {
  cfg: GoogleConfig;
  // Moves the map here (a place search result); changes to `key` move it again.
  goTo?: { key: number; lat: number; lon: number } | null;
  others?: Array<{ id: number; points: Points }>;
}

// The editor page's own map.
export function GeofenceEditor({ cfg, kind, circle, points, colour, onCircle, onPoints, goTo, others = [] }: EditorProps) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map | null>(null);
  const [mapObj, setMapObj] = useState<google.maps.Map | null>(null);
  const otherShapes = useRef<google.maps.Polygon[]>([]);
  useGeofenceDrawing(mapObj, { kind, circle, points, colour, onCircle, onPoints, fitOnStart: true });

  useEffect(() => {
    const dark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const m = new google.maps.Map(box.current!, {
      mapId: cfg.mapId,
      colorScheme: dark ? google.maps.ColorScheme.DARK : google.maps.ColorScheme.LIGHT,
      mapTypeId: readFlag(SAT_KEY, false) ? 'hybrid' : 'roadmap',
      disableDefaultUI: true, zoomControl: true, mapTypeControl: true,
      mapTypeControlOptions: { mapTypeIds: ['roadmap', 'hybrid'], style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR },
      gestureHandling: 'greedy', clickableIcons: false,
    });
    m.addListener('maptypeid_changed', () => saveFlag(SAT_KEY, m.getMapTypeId() === 'hybrid'));
    m.fitBounds({ west: IRELAND.w, south: IRELAND.s, east: IRELAND.e, north: IRELAND.n }, 0);
    map.current = m;
    setMapObj(m);
    return () => {
      google.maps.event.clearInstanceListeners(m);
      otherShapes.current.forEach((o) => o.setMap(null));
      map.current = null;
      setMapObj(null);
    };
  }, []);

  // Other geofences, faint, so a new one can be placed beside them.
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    otherShapes.current.forEach((o) => o.setMap(null));
    const grey = cssVar('--muted') || '#5a6573';
    otherShapes.current = others.map((o) => new google.maps.Polygon({
      map: m, paths: o.points.map(([lon, lat]) => ({ lat, lng: lon })), clickable: false, zIndex: 1,
      strokeColor: grey, strokeWeight: 1, strokeOpacity: 0.7, fillColor: grey, fillOpacity: 0.06,
    }));
  }, [others]);

  useEffect(() => {
    if (!goTo || !map.current) return;
    map.current.setCenter({ lat: goTo.lat, lng: goTo.lon });
    map.current.setZoom(17);
  }, [goTo?.key]);

  return <div ref={box} className="map geo-editor-map" role="application" aria-label="Map for drawing the geofence. Click to place it." />;
}
